import { Typography } from "@material-ui/core";
import { Grid } from "@material-ui/core";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  DangerButton,
  PrimaryButton,
} from "../../../../../../../components/button";
import { NumberField } from "../../../../../../../components/forms/number";
import { TextField } from "../../../../../../../components/forms/text";
import { Icon } from "../../../../../../../components/icons";
import {
  Flex,
  Grid as CustomGrid,
  GridItem,
} from "../../../../../../../components/layout";
import { VerificationDTO } from "../../../../../../../store/verification/models/dto/due-diligence";
import { colours } from "../../../../../../../styles/colours";

type FormType = VerificationDTO.Requests.DueDiligence.Form.Post.Init.T;

const Header = styled(Flex).attrs({ center: true })`
  font-weight: 500;
  padding: 2em 0;
  margin-top: 5em;
  border-top: 1px solid ${colours.lightGrey};
`;

export default function Participants() {
  const { t } = useTranslation("fields");

  const { control, watch, setValue } = useFormContext<FormType>();

  const kycToInput = watch("kycsToInput");

  const { fields, append, remove } = useFieldArray({
    control,
    name: "participants",
  });

  const [prefilledKyc, setPrefilledKyc] = useState(0);

  useEffect(() => {
    setValue("kycsToInput", fields.length + prefilledKyc);
  }, [fields.length, prefilledKyc, setValue]);

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Header>Participant-filled KYC</Header>
          <Typography>
            Enter details of UBO's and/or Shareholders so they will receive
            email to perform the self KYC check as part of the company
            due-diligence.
          </Typography>
        </Grid>
        {fields.map((f, index) => (
          <React.Fragment key={f.id}>
            <Grid item xs={12} md={3}>
              <Controller
                control={control}
                name={`participants.${index}.firstName`}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    error={!!error}
                    helperText={error?.message}
                    fullWidth
                    label={t("firstName")}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Controller
                control={control}
                name={`participants.${index}.lastName`}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    error={!!error}
                    helperText={error?.message}
                    fullWidth
                    label={t("lastName")}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Controller
                control={control}
                name={`participants.${index}.emailAddress`}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    error={!!error}
                    helperText={error?.message}
                    fullWidth
                    type="email"
                    label={t("email")}
                  />
                )}
              />
            </Grid>
            <GridItem item xs={12} md={3}>
              <DangerButton
                fullWidth
                onClick={() => {
                  remove(index);
                }}
              >
                <Icon type="trash" />
              </DangerButton>
            </GridItem>
          </React.Fragment>
        ))}
        <Grid item xs={12}>
          <PrimaryButton
            fullWidth
            onClick={() =>
              append({
                firstName: "",
                lastName: "",
                emailAddress: "",
              })
            }
          >
            Add Another Company Individual
          </PrimaryButton>
        </Grid>
      </Grid>
      <CustomGrid col-sm={2}>
        <div>
          <Header>Admin-filled KYCs</Header>
          <Typography>
            Enter the number of UBO's and/or Shareholders within the company
            that you intend to upload/input manually their KYC as part of the
            company due-diligence.
          </Typography>
          <NumberField
            fullWidth
            onChange={(e) =>
              setPrefilledKyc(
                typeof e.target.value === "number" ? e.target.value : 0
              )
            }
            value={prefilledKyc}
            min={1}
            label=""
          />
        </div>
        <div>
          <Header>Total KYCs</Header>
          <Flex center>
            <span
              style={{
                color: colours.blueGrey,
                fontSize: "2em",
                fontWeight: 700,
              }}
            >
              {kycToInput}
            </span>
          </Flex>
        </div>
      </CustomGrid>
    </>
  );
}
